import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronDown, Eye, EyeOff, Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { useDeferredValue, useState } from "react";

import {
  type AdminCapability,
  type AdminMcpServerStatus,
  type AdminSecret,
  type AdminUser,
  deleteAdminSecret,
  fetchAdminCapabilities,
  fetchAdminMcpStatus,
  fetchAdminSecret,
  fetchAdminSecrets,
  fetchAdminUsers,
  saveAdminSecret,
  updateAllAdminUserCapabilities,
  updateAdminUserRole,
  updateAdminUserStatus,
  updateAdminUserCapability,
} from "@/lib/api";
import { useStore } from "@/lib/store";

import { SidebarToggleButton } from "./SidebarToggleButton";

type Tab = "secrets" | "users" | "mcp";

// ============================================================================
// Secret Modal
// ============================================================================

function SecretModal({
  editKey,
  onClose,
}: {
  editKey: string | null; // null = add, string = edit
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const showToast = useStore((s) => s.showToast);

  const [key, setKey] = useState(editKey ?? "");
  const [value, setValue] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [loaded, setLoaded] = useState(!editKey);

  // Pre-fill when editing
  useQuery({
    queryKey: ["admin", "secret-prefill", editKey],
    queryFn: async () => {
      if (!editKey) return null;
      const data = await fetchAdminSecret(editKey);
      setValue(data.value);
      setCategory(data.category ?? "");
      setDescription(data.description ?? "");
      setLoaded(true);
      return data;
    },
    enabled: !!editKey && !loaded,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      saveAdminSecret(key, {
        value,
        category: category || undefined,
        description: description || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "secrets"] });
      showToast(editKey ? "Secret updated" : "Secret created", "success");
      onClose();
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  const isValid = key.trim().length > 0 && value.length > 0 && loaded;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="border-cyber-border bg-cyber-surface w-full max-w-lg rounded-lg border p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-cyber-text text-lg font-bold">
            {editKey ? "Edit Secret" : "Add Secret"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-cyber-muted hover:text-cyber-text"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-cyber-muted mb-1 block text-xs">Key</label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              disabled={!!editKey}
              placeholder="GRAFANA_TOKEN"
              className="border-cyber-border bg-cyber-bg text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 w-full rounded border px-3 py-2 text-sm focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-cyber-muted mb-1 block text-xs">Value</label>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Secret value..."
              rows={3}
              className="border-cyber-border bg-cyber-bg text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 w-full rounded border px-3 py-2 font-mono text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="text-cyber-muted mb-1 block text-xs">Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value.toLowerCase())}
              placeholder="grafana, hubspot, etc."
              className="border-cyber-border bg-cyber-bg text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 w-full rounded border px-3 py-2 text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="text-cyber-muted mb-1 block text-xs">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Human-readable note"
              className="border-cyber-border bg-cyber-bg text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 w-full rounded border px-3 py-2 text-sm focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="border-cyber-border text-cyber-muted hover:text-cyber-text rounded border px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!isValid || saveMutation.isPending}
            className="border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan hover:bg-cyber-cyan/20 rounded border px-4 py-2 text-sm disabled:opacity-50"
          >
            {saveMutation.isPending ? "Saving..." : editKey ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Secrets Tab
// ============================================================================

function SecretsTab() {
  const queryClient = useQueryClient();
  const showToast = useStore((s) => s.showToast);
  const [modalKey, setModalKey] = useState<string | null | false>(false); // false=closed, null=add, string=edit
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ["admin", "secrets"],
    queryFn: fetchAdminSecrets,
  });
  const deleteMutation = useMutation({
    mutationFn: deleteAdminSecret,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "secrets"] });
      showToast("Secret deleted", "success");
      setDeleteConfirm(null);
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  const toggleReveal = (key: string) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (isPending) {
    return (
      <div className="text-cyber-muted flex items-center justify-center py-12">
        <span className="animate-spin-glow mr-2 inline-block">&#9680;</span> Loading secrets...
      </div>
    );
  }

  const secrets = data?.secrets ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-cyber-muted text-sm">
          {secrets.length} secret{secrets.length !== 1 ? "s" : ""}
        </p>
        <button
          type="button"
          onClick={() => setModalKey(null)}
          className="border-cyber-cyan/30 text-cyber-cyan hover:bg-cyber-cyan/10 flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs"
        >
          <Plus size={14} /> Add Secret
        </button>
      </div>

      {secrets.length === 0 ? (
        <div className="text-cyber-muted py-12 text-center text-sm">
          No secrets configured. Add one to get started.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-cyber-border text-cyber-muted border-b text-xs">
                <th className="pr-4 pb-2">Key</th>
                <th className="pr-4 pb-2">Category</th>
                <th className="pr-4 pb-2">Description</th>
                <th className="pr-4 pb-2">Updated</th>
                <th className="pr-4 pb-2">By</th>
                <th className="pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {secrets.map((secret) => (
                <SecretRow
                  key={secret.key}
                  secret={secret}
                  revealed={revealedKeys.has(secret.key)}
                  onToggleReveal={() => toggleReveal(secret.key)}
                  onEdit={() => setModalKey(secret.key)}
                  onDelete={() => setDeleteConfirm(secret.key)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="border-cyber-border bg-cyber-surface w-full max-w-sm rounded-lg border p-6">
            <h3 className="text-cyber-text mb-2 text-sm font-bold">Delete Secret</h3>
            <p className="text-cyber-muted mb-4 text-sm">
              Delete <span className="text-cyber-text font-mono">{deleteConfirm}</span>? This cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="border-cyber-border text-cyber-muted hover:text-cyber-text rounded border px-3 py-1.5 text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="rounded border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      {modalKey !== false && <SecretModal editKey={modalKey} onClose={() => setModalKey(false)} />}
    </div>
  );
}

function SecretRow({
  secret,
  revealed,
  onToggleReveal,
  onEdit,
  onDelete,
}: {
  secret: AdminSecret;
  revealed: boolean;
  onToggleReveal: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [revealedValue, setRevealedValue] = useState<string | null>(null);

  const handleReveal = async () => {
    if (revealed && revealedValue) {
      setRevealedValue(null);
      onToggleReveal();
      return;
    }
    try {
      const data = await fetchAdminSecret(secret.key);
      setRevealedValue(data.value);
      onToggleReveal();
    } catch {
      // Ignore
    }
  };

  return (
    <tr className="border-cyber-border/50 border-b">
      <td className="text-cyber-text py-2.5 pr-4 font-mono">{secret.key}</td>
      <td className="text-cyber-muted py-2.5 pr-4">{secret.category ?? "-"}</td>
      <td className="text-cyber-muted max-w-[200px] truncate py-2.5 pr-4">
        {secret.description ?? "-"}
      </td>
      <td className="text-cyber-muted py-2.5 pr-4 text-xs">
        {new Date(secret.updatedAt).toLocaleDateString()}
      </td>
      <td className="text-cyber-muted py-2.5 pr-4 text-xs">{secret.updatedBy}</td>
      <td className="py-2.5 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleReveal}
            className="text-cyber-muted hover:text-cyber-text"
            title={revealed ? "Hide value" : "Reveal value"}
          >
            {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="text-cyber-muted hover:text-cyber-cyan"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-cyber-muted hover:text-red-400"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {revealed && revealedValue && (
          <div className="text-cyber-cyan mt-1 max-w-[300px] truncate text-left font-mono text-xs">
            {revealedValue}
          </div>
        )}
      </td>
    </tr>
  );
}

// ============================================================================
// Users Tab
// ============================================================================

const CAPABILITY_GROUP_LABELS: Record<string, string> = {
  clickhouse: "ClickHouse",
};

function groupCapabilities(capabilities: AdminCapability[]) {
  const groups = new Map<string, AdminCapability[]>();

  for (const capability of capabilities) {
    const namespace = capability.id.split(".")[0] ?? capability.id;
    const group = groups.get(namespace) ?? [];
    group.push(capability);
    groups.set(namespace, group);
  }

  return Array.from(groups, ([namespace, items]) => ({
    namespace,
    label:
      CAPABILITY_GROUP_LABELS[namespace] ??
      `${namespace.charAt(0).toUpperCase()}${namespace.slice(1)}`,
    items,
  }));
}

function UsersTab() {
  const queryClient = useQueryClient();
  const showToast = useStore((s) => s.showToast);
  const currentUser = useStore((s) => s.user);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "admin" | "member">("all");
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [statusConfirm, setStatusConfirm] = useState<AdminUser | null>(null);
  const deferredSearch = useDeferredValue(search).trim().toLowerCase();

  const { data, isPending } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: fetchAdminUsers,
  });
  const { data: capabilityData, isPending: capabilitiesPending } = useQuery({
    queryKey: ["admin", "capabilities"],
    queryFn: fetchAdminCapabilities,
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: "admin" | "member" }) =>
      updateAdminUserRole(id, role),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      showToast(`Role updated to ${vars.role}`, "success");
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateAdminUserStatus(id, active),
    onSuccess: async (result, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setStatusConfirm(null);
      showToast(
        vars.active
          ? "Account reactivated"
          : `Account deactivated; ${result.disabledCircuits} ${result.disabledCircuits === 1 ? "Circuit" : "Circuits"} disabled`,
        "success",
      );
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  const capabilityMutation = useMutation({
    mutationFn: ({
      userId,
      capabilityId,
      enabled,
    }: {
      userId: string;
      capabilityId: string;
      enabled: boolean;
    }) => updateAdminUserCapability(userId, capabilityId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "capabilities"] });
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  const bulkCapabilityMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      updateAllAdminUserCapabilities(userId, enabled),
    onSuccess: async (_, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "capabilities"] });
      showToast(`All capabilities ${vars.enabled ? "enabled" : "disabled"}`, "success");
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  if (isPending || capabilitiesPending) {
    return (
      <div className="text-cyber-muted flex items-center justify-center py-12">
        <span className="animate-spin-glow mr-2 inline-block">&#9680;</span> Loading users...
      </div>
    );
  }

  const users = data?.users ?? [];
  const capabilities = capabilityData?.capabilities ?? [];
  const capabilityOverrides = new Map(
    (capabilityData?.overrides ?? []).map(({ userId, capabilityId, enabled }) => [
      `${userId}:${capabilityId}`,
      enabled,
    ]),
  );
  const capabilityEnabled = (userId: string, capability: AdminCapability) =>
    capabilityOverrides.get(`${userId}:${capability.id}`) ?? capability.enabledByDefault !== false;
  const capabilityGroups = groupCapabilities(capabilities);
  const filteredUsers = users.filter((user) => {
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesSearch =
      !deferredSearch ||
      user.name.toLowerCase().includes(deferredSearch) ||
      user.email.toLowerCase().includes(deferredSearch);
    return matchesRole && matchesSearch;
  });

  return (
    <div>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            size={14}
            className="text-cyber-muted pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search users by name or email"
            aria-label="Search users"
            className="border-cyber-border bg-cyber-surface/30 text-cyber-text placeholder:text-cyber-muted/60 focus:border-cyber-cyan/60 w-full rounded border py-2 pr-3 pl-9 text-sm transition-colors outline-none"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value as "all" | "admin" | "member")}
          aria-label="Filter users by role"
          className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-cyan/60 rounded border px-3 py-2 text-sm outline-none"
        >
          <option value="all">All roles</option>
          <option value="admin">Admins</option>
          <option value="member">Members</option>
        </select>
        <span className="text-cyber-muted text-xs whitespace-nowrap">
          {filteredUsers.length} of {users.length} users
        </span>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="border-cyber-border bg-cyber-surface/20 text-cyber-muted rounded-lg border py-12 text-center text-sm">
          No users match these filters.
        </div>
      ) : (
        <div className="border-cyber-border overflow-hidden rounded-lg border">
          {filteredUsers.map((user) => {
            const expanded = expandedUserId === user.id;
            const isDisabled = Boolean(user.disabledAt);
            const enabledCount = capabilities.reduce(
              (count, capability) => count + Number(capabilityEnabled(user.id, capability)),
              0,
            );
            const disabledCount = capabilities.length - enabledCount;
            const capabilitiesUpdating =
              capabilityMutation.isPending || bulkCapabilityMutation.isPending;

            return (
              <div key={user.id} className="border-cyber-border/50 border-b last:border-b-0">
                <div className="grid gap-3 px-3 py-3 md:grid-cols-[minmax(0,1fr)_7rem_8rem_7rem] md:items-center">
                  <button
                    type="button"
                    onClick={() => setExpandedUserId(expanded ? null : user.id)}
                    aria-expanded={expanded}
                    aria-controls={`user-capabilities-${user.id}`}
                    className="group flex min-w-0 items-center gap-3 text-left"
                  >
                    {user.image ? (
                      <img src={user.image} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                    ) : (
                      <div className="bg-cyber-border text-cyber-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs">
                        {user.name.charAt(0).toUpperCase() || "?"}
                      </div>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="text-cyber-text block truncate text-sm font-medium">
                        {user.name}
                      </span>
                      <span className="text-cyber-muted block truncate text-xs">{user.email}</span>
                    </span>
                    <ChevronDown
                      size={16}
                      className={clsx(
                        "text-cyber-muted group-hover:text-cyber-cyan shrink-0 transition-transform",
                        expanded && "rotate-180",
                      )}
                    />
                  </button>

                  <div className="grid grid-cols-1 gap-3 pl-11 sm:grid-cols-3 md:contents">
                    <div>
                      <div className="text-cyber-muted/60 mb-1 text-[10px] tracking-wide uppercase md:hidden">
                        Role
                      </div>
                      <select
                        value={user.role}
                        onChange={(event) =>
                          roleMutation.mutate({
                            id: user.id,
                            role: event.target.value as "admin" | "member",
                          })
                        }
                        disabled={user.id === currentUser?.id || roleMutation.isPending}
                        aria-label={`Role for ${user.email}`}
                        className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-cyan/50 w-full rounded border px-2 py-1 text-xs focus:outline-none disabled:opacity-50"
                      >
                        <option value="member">member</option>
                        <option value="admin">admin</option>
                      </select>
                    </div>
                    <div>
                      <div className="text-cyber-muted/60 mb-1 text-[10px] tracking-wide uppercase md:hidden">
                        Access
                      </div>
                      <div
                        className={clsx(
                          "text-xs whitespace-nowrap",
                          isDisabled
                            ? "text-red-400"
                            : disabledCount === 0
                              ? "text-cyber-muted"
                              : "text-amber-400",
                        )}
                      >
                        {isDisabled
                          ? "Account deactivated"
                          : disabledCount === 0
                            ? `All ${capabilities.length} enabled`
                            : `${disabledCount} disabled`}
                      </div>
                    </div>
                    <div>
                      <div className="text-cyber-muted/60 mb-1 text-[10px] tracking-wide uppercase md:hidden">
                        Joined
                      </div>
                      <div className="text-cyber-muted text-xs whitespace-nowrap">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div
                    id={`user-capabilities-${user.id}`}
                    className="border-cyber-border/50 bg-cyber-bg/40 border-t px-4 py-4 md:px-6"
                  >
                    <div className="border-cyber-border bg-cyber-surface/20 mb-5 flex flex-wrap items-center justify-between gap-3 rounded border p-3">
                      <div>
                        <h3 className="text-cyber-text text-sm font-medium">Account access</h3>
                        <p className="text-cyber-muted mt-0.5 text-xs">
                          {isDisabled
                            ? `Deactivated ${new Date(user.disabledAt!).toLocaleString()}`
                            : "Active — web and CLI/API access is allowed."}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setStatusConfirm(user)}
                        disabled={
                          statusMutation.isPending || (!isDisabled && user.id === currentUser?.id)
                        }
                        className={clsx(
                          "rounded border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                          isDisabled
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                            : "border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20",
                        )}
                      >
                        {isDisabled ? "Reactivate account" : "Deactivate account"}
                      </button>
                    </div>

                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-cyber-text text-sm font-medium">Capabilities</h3>
                        <p className="text-cyber-muted mt-0.5 text-xs">
                          Manage remote access for {user.name}.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <span className="text-cyber-muted mr-1 text-xs">
                          {enabledCount} of {capabilities.length} enabled
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            bulkCapabilityMutation.mutate({ userId: user.id, enabled: true })
                          }
                          disabled={
                            capabilitiesUpdating ||
                            capabilities.length === 0 ||
                            enabledCount === capabilities.length
                          }
                          className="border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan hover:bg-cyber-cyan/20 rounded border px-2.5 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Enable all
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            bulkCapabilityMutation.mutate({ userId: user.id, enabled: false })
                          }
                          disabled={
                            capabilitiesUpdating || capabilities.length === 0 || enabledCount === 0
                          }
                          className="border-cyber-border text-cyber-muted rounded border px-2.5 py-1 text-xs transition-colors hover:border-red-400/40 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Disable all
                        </button>
                      </div>
                    </div>

                    {capabilityGroups.length === 0 ? (
                      <p className="text-cyber-muted text-xs">No capabilities are available.</p>
                    ) : (
                      <div className="space-y-5">
                        {capabilityGroups.map((group) => (
                          <section key={group.namespace}>
                            <h4 className="text-cyber-muted/70 mb-2 text-[10px] tracking-wider uppercase">
                              {group.label} ({group.items.length})
                            </h4>
                            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                              {group.items.map((capability) => (
                                <label
                                  key={capability.id}
                                  className="border-cyber-border bg-cyber-surface/20 hover:border-cyber-cyan/30 flex cursor-pointer items-start gap-3 rounded border p-3 transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={capabilityEnabled(user.id, capability)}
                                    onChange={(event) =>
                                      capabilityMutation.mutate({
                                        userId: user.id,
                                        capabilityId: capability.id,
                                        enabled: event.target.checked,
                                      })
                                    }
                                    disabled={capabilitiesUpdating}
                                    aria-label={`${capability.id} access for ${user.email}`}
                                    className="accent-cyber-cyan mt-0.5 h-4 w-4 shrink-0 disabled:opacity-50"
                                  />
                                  <span className="min-w-0">
                                    <span className="flex flex-wrap items-center gap-1.5">
                                      <span className="text-cyber-text font-mono text-xs break-all">
                                        {capability.id}
                                      </span>
                                      <span
                                        className={clsx(
                                          "rounded px-1.5 py-0.5 text-[9px] tracking-wide uppercase",
                                          capability.access === "write"
                                            ? "bg-cyber-magenta/10 text-cyber-magenta"
                                            : "bg-cyber-cyan/10 text-cyber-cyan",
                                        )}
                                      >
                                        {capability.access}
                                      </span>
                                      {capability.destructive && (
                                        <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[9px] tracking-wide text-red-400 uppercase">
                                          Destructive
                                        </span>
                                      )}
                                      {capability.enabledByDefault === false && (
                                        <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] tracking-wide text-amber-400 uppercase">
                                          Default off
                                        </span>
                                      )}
                                    </span>
                                    <span className="text-cyber-muted mt-1 block text-[11px] leading-4">
                                      {capability.description}
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {statusConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="border-cyber-border bg-cyber-surface w-full max-w-md rounded-lg border p-6">
            <h3 className="text-cyber-text mb-2 text-sm font-bold">
              {statusConfirm.disabledAt ? "Reactivate Account" : "Deactivate Account"}
            </h3>
            <p className="text-cyber-muted mb-4 text-sm">
              {statusConfirm.disabledAt ? (
                <>
                  Reactivate <span className="text-cyber-text">{statusConfirm.email}</span>? They
                  can sign in again, but previously disabled API keys will remain disabled.
                </>
              ) : (
                <>
                  Deactivate <span className="text-cyber-text">{statusConfirm.email}</span>? Their
                  sessions will be revoked, API keys disabled, and all Circuits disabled
                  immediately.
                </>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setStatusConfirm(null)}
                disabled={statusMutation.isPending}
                className="border-cyber-border text-cyber-muted hover:text-cyber-text rounded border px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  statusMutation.mutate({
                    id: statusConfirm.id,
                    active: Boolean(statusConfirm.disabledAt),
                  })
                }
                disabled={statusMutation.isPending}
                className={clsx(
                  "rounded border px-3 py-1.5 text-xs disabled:opacity-40",
                  statusConfirm.disabledAt
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                    : "border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20",
                )}
              >
                {statusMutation.isPending
                  ? "Updating..."
                  : statusConfirm.disabledAt
                    ? "Reactivate"
                    : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MCP Tab
// ============================================================================

const MCP_STATUS_LABELS: Record<AdminMcpServerStatus["status"], string> = {
  connected: "Connected",
  disabled: "Disabled",
  failed: "Failed",
  needs_auth: "Authentication required",
  needs_client_registration: "Client registration required",
};

const MCP_AUTH_LABELS: Record<AdminMcpServerStatus["authState"], string> = {
  ready: "Ready",
  required: "Required",
  client_registration_required: "Client setup required",
  not_checked: "Not checked",
  unknown: "Unknown",
};

function mcpStatusTone(status: AdminMcpServerStatus["status"]): string {
  switch (status) {
    case "connected":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-400";
    case "needs_auth":
    case "needs_client_registration":
      return "border-amber-500/30 bg-amber-500/10 text-amber-400";
    case "failed":
      return "border-red-500/30 bg-red-500/10 text-red-400";
    case "disabled":
      return "border-cyber-border bg-cyber-muted/10 text-cyber-muted";
  }
}

function McpTab() {
  const { data, error, isPending, isFetching, refetch } = useQuery({
    queryKey: ["admin", "mcp"],
    queryFn: fetchAdminMcpStatus,
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: false,
  });

  if (isPending) {
    return (
      <div className="text-cyber-muted flex items-center justify-center py-12">
        <span className="animate-spin-glow mr-2 inline-block">&#9680;</span> Checking OpenCode MCP
        servers...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-8 text-center">
        <p className="text-sm text-red-400">Unable to read MCP status from OpenCode.</p>
        <p className="text-cyber-muted mt-1 text-xs">
          Confirm the configured OpenCode server is running and reachable.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="border-cyber-border text-cyber-muted hover:border-cyber-cyan/40 hover:text-cyber-cyan mt-4 rounded border px-3 py-1.5 text-xs transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const availableCount = data.servers.filter((server) => server.available).length;
  const enabledCount = data.servers.filter((server) => server.enabled).length;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-cyber-text text-sm">
            {availableCount} of {data.servers.length} MCP servers available
          </p>
          <p className="text-cyber-muted mt-0.5 text-xs">
            Live status from the running OpenCode server · {enabledCount} enabled
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="border-cyber-border text-cyber-muted hover:border-cyber-cyan/40 hover:text-cyber-cyan flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {data.servers.length === 0 ? (
        <div className="border-cyber-border bg-cyber-surface/20 rounded-lg border py-12 text-center">
          <p className="text-cyber-muted text-sm">No MCP servers are configured in OpenCode.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.servers.map((server) => (
            <section
              key={server.name}
              className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-cyber-text min-w-0 truncate font-mono text-sm font-medium">
                  {server.name}
                </h3>
                <span
                  className={clsx(
                    "shrink-0 rounded border px-1.5 py-0.5 text-[9px] tracking-wide uppercase",
                    mcpStatusTone(server.status),
                  )}
                >
                  {MCP_STATUS_LABELS[server.status]}
                </span>
              </div>

              <dl className="mt-4 space-y-2 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-cyber-muted">Enabled</dt>
                  <dd className={server.enabled ? "text-emerald-400" : "text-cyber-muted"}>
                    {server.enabled ? "Yes" : "No"}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-cyber-muted">Available</dt>
                  <dd className={server.available ? "text-emerald-400" : "text-red-400"}>
                    {server.available ? "Yes" : "No"}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-cyber-muted">Authentication</dt>
                  <dd className="text-cyber-text text-right">
                    {MCP_AUTH_LABELS[server.authState]}
                  </dd>
                </div>
              </dl>

              {server.error && (
                <p className="mt-3 rounded border border-red-500/20 bg-red-500/5 p-2 text-[11px] break-words text-red-300">
                  {server.error}
                </p>
              )}
            </section>
          ))}
        </div>
      )}

      <section className="border-cyber-border bg-cyber-surface/20 mt-5 rounded-lg border p-4">
        <h3 className="text-cyber-text text-sm font-medium">Update MCP credentials</h3>
        <p className="text-cyber-muted mt-1 text-xs leading-5">
          SSH into the Sherlog machine, then update the OpenCode user service override. Keep
          credentials on the host; do not enter them in this admin page.
        </p>
        <ol className="text-cyber-muted mt-3 space-y-3 text-xs">
          <li>
            <span className="text-cyber-text block">1. SSH into the Sherlog machine</span>
          </li>
          <li>
            <span className="text-cyber-text mb-1 block">2. Edit the service override</span>
            <pre className="border-cyber-border bg-cyber-bg text-cyber-cyan overflow-x-auto rounded border p-2 font-mono">
              {"systemctl --user edit opencode.service"}
            </pre>
          </li>
          <li>
            <span className="text-cyber-text mb-1 block">3. Reload the user systemd manager</span>
            <pre className="border-cyber-border bg-cyber-bg text-cyber-cyan overflow-x-auto rounded border p-2 font-mono">
              {"systemctl --user daemon-reload"}
            </pre>
          </li>
          <li>
            <span className="text-cyber-text mb-1 block">4. Restart OpenCode</span>
            <pre className="border-cyber-border bg-cyber-bg text-cyber-cyan overflow-x-auto rounded border p-2 font-mono">
              {"systemctl --user restart opencode.service"}
            </pre>
          </li>
        </ol>
        <p className="text-cyber-muted mt-3 text-xs">
          After the restart, use Refresh above to confirm that each MCP server is connected.
        </p>
      </section>

      <p className="text-cyber-muted/60 mt-4 text-right text-[10px]">
        Checked {new Date(data.checkedAt).toLocaleTimeString()}
      </p>
    </div>
  );
}

// ============================================================================
// Admin View
// ============================================================================

export function AdminView() {
  const [tab, setTab] = useState<Tab>("users");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="border-cyber-border border-b py-2 pr-2 pl-0 md:px-4 md:py-3">
        <div className="mx-auto flex max-w-4xl items-center gap-2 px-2">
          <SidebarToggleButton />
          <h1 className="text-cyber-text text-sm font-bold">Admin</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-6xl">
          {/* Tabs */}
          <div className="border-cyber-border mb-6 flex gap-1 border-b">
            <button
              type="button"
              onClick={() => setTab("users")}
              className={clsx(
                "px-4 py-2 text-sm transition-colors",
                tab === "users"
                  ? "border-cyber-cyan text-cyber-cyan border-b-2"
                  : "text-cyber-muted hover:text-cyber-text",
              )}
            >
              Users
            </button>
            <button
              type="button"
              onClick={() => setTab("secrets")}
              className={clsx(
                "px-4 py-2 text-sm transition-colors",
                tab === "secrets"
                  ? "border-cyber-cyan text-cyber-cyan border-b-2"
                  : "text-cyber-muted hover:text-cyber-text",
              )}
            >
              Secrets
            </button>
            <button
              type="button"
              onClick={() => setTab("mcp")}
              className={clsx(
                "px-4 py-2 text-sm transition-colors",
                tab === "mcp"
                  ? "border-cyber-cyan text-cyber-cyan border-b-2"
                  : "text-cyber-muted hover:text-cyber-text",
              )}
            >
              MCP
            </button>
          </div>

          {tab === "users" ? <UsersTab /> : tab === "secrets" ? <SecretsTab /> : <McpTab />}
        </div>
      </div>
    </div>
  );
}
